import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@pushpress/pushpress-ui";
import { getFirstAndLastInitials, getFullName } from "../../../utils/names";
import { cn } from "../../../utils/css";

export interface UserActivityInfo {
  firstName: string;
  lastName: string;
  userImageUrl: string;
}

interface UserActivityAvatarProps {
  maxVisibleIcons: number;
  isLoading: boolean;
  users?: UserActivityInfo[];
}

const UserActivityAvatars: React.FC<UserActivityAvatarProps> = ({
  maxVisibleIcons,
  isLoading,
  users = [],
}) => {
  if (isLoading) {
    return (
      <div className="tw-flex tw--space-x-1 tw-overflow-hidden">
        {Array.from({ length: maxVisibleIcons }).map((_, index) => (
          <div
            key={`dashboard-activity-avatar-group-skeleton-${index}`}
            className={cn(
              "tw-h-10 tw-w-10 tw-rounded-full tw-bg-gray-200",
              "tw-animate-pulse tw-ring-2 tw-ring-white",
            )}
          />
        ))}
      </div>
    );
  }

  if (!users || users.length === 0) {
    return null;
  }

  const visibleUserCount =
    users.length > maxVisibleIcons ? maxVisibleIcons - 1 : users.length;
  const visibleUsers = users.slice(0, visibleUserCount);
  const remainingUsersCount = users.length - visibleUsers.length;
  const remainingUsersText =
    remainingUsersCount > 99 ? "99+" : `${remainingUsersCount}+`;

  return (
    <div className="tw-flex tw--space-x-1 tw-overflow-hidden">
      {visibleUsers.map((user, index) => {
        const memberInitials = getFirstAndLastInitials([
          user.firstName,
          user.lastName,
        ]);
        const memberFullName = getFullName([user.firstName, user.lastName]);

        return (
          <TooltipProvider
            key={`dashboard-activity-avatar-${user.firstName}-${user.lastName}-${index}`}
          >
            <Tooltip>
              <TooltipTrigger>
                <Avatar className="tw-border tw-ring-2 tw-ring-white tw-object-cover">
                  <AvatarImage src={user.userImageUrl} />
                  <AvatarFallback>{memberInitials}</AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent className="!tw-bg-muted !tw-text-gray-800">
                <span>{memberFullName}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}

      {remainingUsersCount > 0 && (
        <Avatar className="tw-ring-2 tw-ring-white">
          <AvatarFallback>{remainingUsersText}</AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

export default UserActivityAvatars;
